import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { Loader2, Shield } from 'lucide-react';
import JourneyLayout from '../../components/JourneyLayout';

function AccountAggregatorRedirect() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkLoginStatus = async () => {
      setCheckingAuth(true);
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('authtoken');
      const profileId = localStorage.getItem('profile_id') || '';
      
      if (!token || !profileId) {
        navigate('/apply-now');
        return;
      }
      setCheckingAuth(false);
    };

    checkLoginStatus();
  }, [navigate]);

  useEffect(() => {
    if (checkingAuth) return;

    let pollInterval = null;
    let timeoutId = null;
    const POLL_INTERVAL = 5000; // 5 seconds
    const MAX_POLLING_TIME = 25000; // 25 seconds total

    const checkAccountAggregatorStatus = async () => {
      const profileId = localStorage.getItem('profile_id') || '';
      if (!profileId) {
        setError('Session expired. Please login again.');
        setTimeout(() => navigate('/apply-now'), 2000);
        return;
      }

      try {
        // First, get dashboard data to fetch lead_id (only once)
        const dashboardResponse = await authAPI.getDashboardData(profileId);
        
        if (dashboardResponse.Status !== 1) {
          setError('Failed to fetch dashboard data. Please try again.');
          setLoading(false);
          return;
        }

        const leadId = dashboardResponse.Data?.profile_details?.lead_id || '';
        
        // Function to cleanup polling
        const cleanupPolling = () => {
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        };
        
        // Function to check status
        const checkStatus = async () => {
          try {
            const response = await authAPI.checkAccountAggregatorStatus({
              profile_id: profileId,
              lead_id: leadId,
            });

            if (response.Status === 1) {
              // Success - navigate to banking details page
              cleanupPolling();
              navigate('/banking-details');
            } else if (response.Status === 2 && response.Data?.next_step === 'check_account_aggregator') {
              // Keep polling - Status is 2 and next_step is check_account_aggregator
              // Continue polling - interval will call again
            } else if (response.Status === 2 && response.Data?.next_step === 'initiate_account_aggregator') {
              // Navigate to account-aggregator
              cleanupPolling();
              navigate('/account-aggregator');
            } else {
              // Other error cases
              cleanupPolling();
              setError(response.Message || 'Failed to check Account Aggregator status. Please try again.');
              setLoading(false);
            }
          } catch (err) {
            cleanupPolling();
            setError(err.message || 'Failed to check Account Aggregator status. Please try again.');
            setLoading(false);
          }
        };

        // Initial check
        const initialResponse = await authAPI.checkAccountAggregatorStatus({
          profile_id: profileId,
          lead_id: leadId,
        });

        if (initialResponse.Status === 1) {
          // Success immediately
          navigate('/banking-details');
        } else if (initialResponse.Status === 2 && initialResponse.Data?.next_step === 'check_account_aggregator') {
          // Start polling with 25 second timeout
          pollInterval = setInterval(checkStatus, POLL_INTERVAL);
          
          // Set timeout to navigate to account-aggregator after 25 seconds
          timeoutId = setTimeout(() => {
            cleanupPolling();
            navigate('/account-aggregator');
          }, MAX_POLLING_TIME);
        } else if (initialResponse.Status === 2 && initialResponse.Data?.next_step === 'initiate_account_aggregator') {
          navigate('/account-aggregator');
        } else {
          setError(initialResponse.Message || 'Failed to check Account Aggregator status. Please try again.');
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Failed to check Account Aggregator status. Please try again.');
        setLoading(false);
      }
    };

    checkAccountAggregatorStatus();

    // Cleanup function
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [navigate, checkingAuth]);

  if (checkingAuth) {
    return (
      <JourneyLayout journeyStep={5}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-theme" />
        </div>
      </JourneyLayout>
    );
  }

  if (loading) {
    return (
      <JourneyLayout journeyStep={5}>
        <div className="text-center space-y-6 py-12">
          <div className="flex justify-center">
            <div className="bg-theme/10 p-4 rounded-full">
              <Shield className="h-12 w-12 text-theme" />
            </div>
          </div>
          <Loader2 className="h-10 w-10 text-theme animate-spin mx-auto" />
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Checking Status</h2>
          <p className="text-gray-600 text-lg">Verifying Account Aggregator status...</p>
          <p className="text-gray-500 text-sm">Please wait while we process your request</p>
          <p className="text-gray-400 text-xs">This may take a few moments</p>
        </div>
      </JourneyLayout>
    );
  }

  if (error) {
    return (
      <JourneyLayout journeyStep={5}>
        <div className="text-center space-y-6 py-12">
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 max-w-md mx-auto">
            <p className="text-red-700 font-semibold">{error}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-theme to-theme2 text-white px-6 py-3 rounded-2xl font-semibold hover:opacity-90 transition-opacity shadow-lg"
          >
            Back to Home
          </button>
        </div>
      </JourneyLayout>
    );
  }

  return null;
}

export default AccountAggregatorRedirect;

